import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { IDLE_LIMIT_MS, LAST_ACTIVITY_KEY, LOGIN_AT_KEY, sessionState } from "@/lib/sessionTimeout";

const readNum = (k: string) => Number(localStorage.getItem(k)) || 0;

/** Signs dashboard users out after inactivity or a maximum session length. */
export const SessionTimeoutGuard = () => {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const signingOut = useRef(false);

  useEffect(() => {
    const now = Date.now();
    if (!readNum(LOGIN_AT_KEY)) localStorage.setItem(LOGIN_AT_KEY, String(now));
    if (!readNum(LAST_ACTIVITY_KEY)) localStorage.setItem(LAST_ACTIVITY_KEY, String(now));

    let lastWrite = 0;
    const onActivity = () => {
      const t = Date.now();
      if (t - lastWrite < 15000) return;
      // Don't let activity revive an expired session.
      if (sessionState(t, readNum(LAST_ACTIVITY_KEY), readNum(LOGIN_AT_KEY)) === "expired") return;
      lastWrite = t;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(t));
    };
    const events = ["pointerdown", "keydown", "scroll", "wheel", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true, capture: true }));

    const expire = async () => {
      if (signingOut.current) return;
      signingOut.current = true;
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      localStorage.removeItem(LOGIN_AT_KEY);
      await supabase.auth.signOut();
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.assign(`/auth?reason=timeout&next=${next}`);
    };

    const tick = () => {
      const t = Date.now();
      const last = readNum(LAST_ACTIVITY_KEY) || t;
      const state = sessionState(t, last, readNum(LOGIN_AT_KEY) || t);
      if (state === "expired") return void expire();
      setWarning(state === "warning");
      if (state === "warning") setSecondsLeft(Math.max(0, Math.ceil((last + IDLE_LIMIT_MS - t) / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity, { capture: true } as EventListenerOptions));
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);

  const stay = () => { localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); setWarning(false); };
  const signOutNow = async () => {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    localStorage.removeItem(LOGIN_AT_KEY);
    await supabase.auth.signOut();
    window.location.assign("/auth");
  };

  const m = Math.floor(secondsLeft / 60), s = String(secondsLeft % 60).padStart(2, "0");
  return (
    <AlertDialog open={warning}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you still there?</AlertDialogTitle>
          <AlertDialogDescription>
            For security you'll be signed out in {m}:{s} because of inactivity. Save any work you want to keep.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={signOutNow}>Sign out</AlertDialogCancel>
          <AlertDialogAction onClick={stay}>Stay signed in</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
